import { useCallback } from "react";
import { t } from "ttag";

import { Box, Button, Group, Stack, Text, TextInput } from "metabase/ui";

import { isValidHexColor } from "./validation";

/** Matches `::chart-colors` in src/metabase/agavepulse/branding.clj. */
const MAX_CHART_COLORS = 24;

export interface ChartColorsFieldProps {
  value: string[];
  disabled?: boolean;
  onChange: (colors: string[]) => void;
}

/**
 * Editor for a dashboard's chart accent series.
 *
 * Order matters: the first entry becomes accent0 (the first series in every chart), the
 * second accent1, and so on. Anything not overridden falls back to the AGAVE defaults, so
 * a client can set two colors and leave the rest alone.
 */
export function ChartColorsField({
  value,
  disabled,
  onChange,
}: ChartColorsFieldProps) {
  const setAt = useCallback(
    (index: number, raw: string) => {
      const next = [...value];
      next[index] = raw;
      onChange(next);
    },
    [value, onChange],
  );

  const add = useCallback(() => onChange([...value, ""]), [value, onChange]);

  const removeAt = useCallback(
    (index: number) => onChange(value.filter((_, i) => i !== index)),
    [value, onChange],
  );

  return (
    <Stack gap="xs">
      <Text fw="bold" size="sm">
        {t`Chart colors`}
      </Text>
      <Text size="xs" c="text-medium">
        {t`Series colors, in order. The first is used for the first series in every chart.`}
      </Text>

      {value.map((color, index) => {
        const invalid = color.length > 0 && !isValidHexColor(color);

        return (
          <Group key={index} align="center" wrap="nowrap" gap="sm">
            <Box
              aria-hidden
              w="1.75rem"
              h="1.75rem"
              style={{
                flexShrink: 0,
                borderRadius: "0.25rem",
                border: "1px solid var(--mb-color-border)",
                background: isValidHexColor(color)
                  ? color
                  : "var(--mb-color-background-light)",
              }}
            />
            <TextInput
              flex={1}
              aria-label={t`Series ${index + 1} color`}
              placeholder={t`AGAVE default`}
              value={color}
              disabled={disabled}
              error={invalid ? true : undefined}
              onChange={(event) => setAt(index, event.currentTarget.value)}
            />
            <Button
              variant="subtle"
              size="compact-sm"
              disabled={disabled}
              aria-label={t`Remove series ${index + 1} color`}
              onClick={() => removeAt(index)}
            >
              {t`Remove`}
            </Button>
          </Group>
        );
      })}

      <Group>
        <Button
          variant="subtle"
          size="compact-sm"
          disabled={disabled || value.length >= MAX_CHART_COLORS}
          onClick={add}
        >
          {t`Add a color`}
        </Button>
      </Group>
    </Stack>
  );
}
